import { useState, useEffect, useRef, useCallback } from 'react';
import { debounce } from 'lodash'; // A helpful utility for performance
import { apiCall } from '../utils/api';

const useTags = (initialTags = []) => {
  const [selectedTags, setSelectedTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const tagInputRef = useRef(null);
  const suggestionBoxRef = useRef(null);

  // Function to add a tag
  const addTag = (tag) => {
    tag = tag.trim();
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags((prevTags) => [...prevTags, tag]);
    }
    setTagInput(''); // Clear input after adding
    setSuggestions([]); // Clear suggestions
  };

  // Function to remove a tag
  const removeTag = (tagToRemove) => {
    setSelectedTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
  };

  // Fetch tag suggestions from the backend
  const fetchTagSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const data = await apiCall(`/tags?query=${query}`);
      // The backend returns objects like [{ Tag: "network" }], we need just the string
      const tagStrings = data.map(item => item.Tag);
      
      // Filter out any suggestions that are already selected
      const filteredSuggestions = tagStrings.filter(s => !selectedTags.includes(s));
      setSuggestions(filteredSuggestions);

    } catch (error) {
      console.error("Failed to fetch tag suggestions:", error);
      setSuggestions([]);
    }
  }, [selectedTags]);

  // A debounced version of the fetch function to avoid too many API calls
  const debouncedFetch = useCallback(() => debounce(fetchTagSuggestions, 300), [fetchTagSuggestions]);

  // Handle input change
  const handleTagInputChange = (event) => {
    const value = event.target.value;
    setTagInput(value);
    debouncedFetch(value);
  };

  // Handle key press (Enter or comma to add tag)
  const handleTagInputKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(tagInput);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (tag) => {
    addTag(tag);
    tagInputRef.current.focus();
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionBoxRef.current &&
        !suggestionBoxRef.current.contains(event.target) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return {
    selectedTags,
    setSelectedTags,
    tagInput,
    suggestions,
    tagInputRef,
    suggestionBoxRef,
    addTag,
    removeTag,
    handleTagInputChange,
    handleTagInputKeyPress,
    handleSuggestionClick,
  };
};

export default useTags;